import { Controller } from "@hotwired/stimulus";

// Report::Chart::TIME_DIMENSIONS. A time breakdown with no split reads as a trend, so it defaults to
// a line where any other breakdown defaults to bars.
const TIME_DIMENSIONS = ["hour", "business_date"];

// Mirrors the default the limit select renders selected (see _live_metric_controls). An
// unbounded breakdown is refused without a limit, so re-deselecting the option has to put
// one back rather than leave the field blank.
const DEFAULT_LIMIT = "10";

// The live-metric picker, shared by the tenant board's dialog and the canonical authoring form.
// Subclasses supply add()/submit and the preview URL; everything about SELECTING a metric is here.
//
// Kept as a base rather than two copies because the copies had ALREADY drifted -- the canonical one
// carried two fixes the tenant one never got.
export default class LiveMetricController extends Controller {
  // hiddenDimension is declared HERE, not per subclass: the shared controls partial renders it for
  // both surfaces, so a subclass that omitted it left the input frozen at its render-time value and
  // posting a `dimension` that disagreed with the selection.
  static targets = ["metric", "breakdownGroup", "dimension", "name", "preview",
                    "series", "seriesField", "companion", "companionField", "limit", "limitField",
                    "companionHint", "rollup", "chartType", "chartTypeField", "sort", "sortField",
                    "grainNote", "targetLinesField", "rollupField", "refineEmpty",
                    "hiddenDimension"];

  static values = { metrics: Array, previewUrl: String, unbounded: { type: Array, default: [] } };

  // Enough to collapse a burst of arrow-key presses through the metric list into one read, short enough
  // that a deliberate single change still feels immediate.
  static PREVIEW_DEBOUNCE_MS = 250;

  syncAll() {
    this.showBreakdownsFor(this.metricTarget.value);
    this.syncDependents();
    this.syncGeneratedCardType();
    this.syncGrainNote();
  }

  // The limit settles FIRST: the split (a ranked list cannot be split) and the appearance sync (a ranked
  // default is a table) both read it to decide what to offer. Run after them, they decide against the old
  // value -- and on init an existing bounded split card looks ranked, because the form renders the limit
  // select at its default 8, so its valid split is cleared before the limit is corrected.
  syncDependents() {
    this.syncLimitField();
    this.syncSeriesOptions();
    this.syncCompanionOptions();
    this.syncAppearanceFields();
  }

  metricChanged() {
    // AFTER showBreakdownsFor: a new metric clears the breakdown, and every dependent sync reads
    // selectedDimension(), so running them first computes the shape against the outgoing dimension.
    this.showBreakdownsFor(this.metricTarget.value);
    this.syncHiddenDimension();
    this.syncGrainNote();
    this.syncDependents();
    this.syncGeneratedCardType();
    this.syncName();
    this.schedulePreview();
  }

  dimensionChanged() {
    this.syncGrainNote();
    this.syncHiddenDimension();
    this.syncDependents();
    this.syncGeneratedCardType();
    this.syncName();
    this.schedulePreview();
  }

  seriesChanged() {
    if (this.clearing) return;

    // Mutually exclusive with a companion (see MetricQuery), so choosing one clears the other rather
    // than letting the dialog submit a pair the server refuses.
    if (this.selectedSeries()) this.clearCompanion();
    this.syncCompanionOptions();
    this.syncGeneratedCardType();
    this.syncName();
    this.schedulePreview();
  }

  companionChanged() {
    if (this.clearing) return;

    if (this.selectedCompanion()) this.clearSeries();
    this.syncSeriesOptions();
    this.syncGeneratedCardType();
    // The companion narrows which chart types are valid, so the appearance filter has to re-run when it
    // changes -- otherwise Table stays selectable on an unranked comparison the server refuses.
    this.syncAppearanceFields();
    this.syncName();
    this.schedulePreview();
  }

  limitChanged() {
    // Re-clicking the selected option DESELECTS it (the shared select does that whenever its hidden
    // input is not required), which would leave an unbounded breakdown ranked with no limit -- refused
    // as "item is unbounded and needs a limit". syncLimitField puts the default back.
    this.syncLimitField();
    this.syncAppearanceFields();
    this.schedulePreview();
  }

  rollupChanged() {
    this.schedulePreview();
  }

  appearanceChanged(event) {
    // Touching Show as makes the type the author's, the way typing a name makes the name theirs.
    if (!this.clearing && event?.target === this.chartTypeTarget) this.cardTypeIsGenerated = false;
    // Re-decides the goal-lines field first: switching Show as to a table leaves it visible otherwise,
    // and its value keeps riding along in the preview and save payloads for a shape that cannot draw
    // reference lines at all.
    this.syncTargetLinesField();
    this.schedulePreview();
  }

  // Re-derived while the author has not picked one: an hour breakdown preselects `line`, and adding a
  // split makes it a bar server-side.
  syncGeneratedCardType() {
    if (!this.hasChartTypeTarget || this.cardTypeIsGenerated === false) return;
    // A blank means "let the server decide", which the tenant dialog relies on -- it has no Show as
    // control, so filling one in would submit a shape the author never chose.
    if (this.chartTypeTarget.value === "") return;

    const wanted = this.defaultCardType();
    if (this.chartTypeTarget.value !== wanted) {
      this.resetSelect(this.chartTypeFieldTarget || this.element, this.chartTypeTarget, wanted);
    }
  }

  // Once the user types their own name it is theirs -- without this the next metric/breakdown change
  // silently overwrites it. Clearing the field hands naming back to the generator.
  nameEdited() {
    this.nameIsGenerated = this.nameTarget.value === "";
  }

  showBreakdownsFor(metricKey) {
    // Read from the group being SHOWN, not from selectedDimension(): before the first pass nothing is
    // disabled yet, so that finds another metric's input and the line below would wipe a seeded
    // selection an edited card arrived with.
    const previous = this.checkedDimensionIn(metricKey) || this.selectedDimension();
    const metric = this.metricsValue.find((entry) => entry.key === metricKey);
    const keep = metric && metric.dimensions.includes(previous) ? previous : "";

    this.breakdownGroupTargets.forEach((group) => {
      const active = group.dataset.metricKey === metricKey;
      group.classList.toggle("hidden", !active);
      group.querySelectorAll("input[name*='live_metric_dimension']").forEach((input) => {
        input.disabled = !active;
      });
      // Through the select's own controller: it renders the trigger from a private field, so writing
      // the hidden input directly leaves the control showing a stale label.
      if (active) this.resetSelect(group, group.querySelector("input[name*='live_metric_dimension']"), keep);
    });
  }

  // The debounce timer is set here, so it is cleared here: left to the subclasses, a new one inherits
  // the leak by default and its pending callback fires loadPreview() against a detached preview target
  // after the dialog closes. Subclasses that override this must call super.disconnect().
  disconnect() {
    clearTimeout(this.previewTimeout);
  }

  // The per-metric pickers are named uniquely so their ids stay distinct, so none of them IS the form's
  // `dimension` field -- this input carries the selection. Presence-guarded rather than assumed: the
  // input only exists where the shared controls partial rendered it.
  syncHiddenDimension() {
    if (!this.hasHiddenDimensionTarget) return;

    this.hiddenDimensionTarget.value = this.selectedDimension();
  }

  // The value held by one metric's own picker, ignoring every other metric's.
  checkedDimensionIn(metricKey) {
    const group = this.breakdownGroupTargets.find((g) => g.dataset.metricKey === metricKey);
    const checked = group && group.querySelector("input[name*='live_metric_dimension']");
    return checked ? checked.value : "";
  }

  // Driven through the shadcn select's own markup, not a native <select>: the component renders option
  // divs plus a hidden input carrying the value, so there is no `options` collection to filter.
  syncSeriesOptions() {
    if (!this.hasSeriesFieldTarget) return;

    const dimension = this.selectedDimension();
    const metric = this.selectedMetric();
    // A metric can only be split by a dimension it carries, and never by the breakdown itself (grouping
    // twice by one column draws one point per series). `splittable` is a registry fact -- a rate has no
    // parts that add up -- and a ranked list cannot also be split.
    const ranked = this.hasLimitTarget && this.limitTarget.value !== "";
    const allowed = dimension && metric.splittable && !ranked
      ? metric.dimensions.filter((d) => d !== dimension)
      : [];

    this.seriesFieldTarget.classList.toggle("hidden", allowed.length === 0);
    this.seriesFieldTarget.querySelectorAll("[data-select-value]").forEach((option) => {
      const value = option.dataset.selectValue;
      // The WRAPPER, not the inner option: the select's keyboard navigation skips a choice by checking
      // its closest [data-ui--filter-target='item'] for `hidden`, so hiding only the inner element
      // leaves the option arrow-reachable and selectable.
      const hideable = option.closest("[data-ui--filter-target='item']") || option;
      hideable.classList.toggle("hidden", value !== "" && !allowed.includes(value));
    });
    // A split that is no longer valid falls back to none rather than submitting a pair the server rejects.
    if (this.selectedSeries() && !allowed.includes(this.selectedSeries())) {
      this.clearSeries();
    }
  }

  // A companion is a second metric over the same buckets, so it needs a breakdown, cannot ride alongside
  // a split, and must share the primary's grain -- line money against a sale count answers a different
  // question than the axis claims.
  syncCompanionOptions() {
    if (!this.hasCompanionFieldTarget) return;

    const metric = this.selectedMetric();
    const usable = this.selectedDimension() && !this.selectedSeries() && metric;
    const allowed = usable
      ? this.metricsValue.filter((m) => m.grain === metric.grain && m.key !== metric.key).map((m) => m.key)
      : [];

    this.companionFieldTarget.classList.toggle("hidden", allowed.length === 0);
    this.companionFieldTarget.querySelectorAll("[data-select-value]").forEach((option) => {
      const value = option.dataset.selectValue;
      // Same wrapper rule as the split options above -- keyboard navigation reads the wrapper.
      const hideable = option.closest("[data-ui--filter-target='item']") || option;
      hideable.classList.toggle("hidden", value !== "" && !allowed.includes(value));
    });
    if (this.selectedCompanion() && !allowed.includes(this.selectedCompanion())) {
      this.clearCompanion();
    }
  }

  // A ranked companion is a table column, not a right-hand axis. Saying "line over the bars" there
  // describes a chart the card will never render.
  syncCompanionHint(ranked) {
    if (!this.hasCompanionHintTarget) return;

    this.companionHintTarget.textContent = ranked
      ? "Adds a second column to the table."
      : "Adds a line over the bars, on its own right-hand axis.";
  }

  // An unbounded breakdown (a catalogue) is only readable ranked and capped, and MetricQuery refuses
  // one without a limit -- so the control appears exactly for those, and the value always rides along.
  syncLimitField() {
    if (!this.hasLimitFieldTarget) return;

    const needed = this.unboundedValue.includes(this.selectedDimension());
    this.limitFieldTarget.classList.toggle("hidden", !needed);
    // Cleared rather than merely hidden: a stale value would ride along on submit and silently truncate
    // a breakdown whose vocabulary is already bounded.
    if (this.hasLimitTarget && !needed && this.limitTarget.value !== "") {
      this.resetSelect(this.limitFieldTarget, this.limitTarget, "");
    }
    if (this.hasLimitTarget && needed && !this.limitTarget.value) {
      this.resetSelect(this.limitFieldTarget, this.limitTarget, DEFAULT_LIMIT);
    }
    // The rollup checkbox lives INSIDE the limit section, and hiding a section is CSS only -- a checked
    // box still posts rollup=1 alongside the limit we just cleared, which BuildRealtimeChart refuses as
    // "a rollup needs a limit".
    if (this.hasRollupTarget && !needed) this.rollupTarget.checked = false;
    this.syncRollupField();
    this.syncCompanionHint(needed);
  }

  // MIRRORS Report::Chart.realtime_card_type. Duplicated deliberately: the server still decides on save,
  // and this only has to agree well enough to preselect the same option -- if it ever drifts, the
  // server's answer wins and the author sees it in the preview.
  defaultCardType() {
    const dimension = this.selectedDimension();
    if (dimension === "") return "scalar";
    if (this.hasLimitTarget && this.limitTarget.value) return "table";
    // An unranked comparison is bars with a second axis, never a line -- mirrors the same branch in
    // Report::Chart.build_realtime and BuildRealtimeChart#derived_card_type.
    if (this.selectedCompanion()) return "bar";
    if (TIME_DIMENSIONS.includes(dimension) && !this.selectedSeries()) return "line";

    return "bar";
  }

  // "Show as" stays and swaps its options -- a breakdown offers the chart forms, a plain total offers
  // Number -- but ordering is hidden outright, since there is nothing to order in a single row.
  syncAppearanceFields() {
    const grouped = this.selectedDimension() !== "";
    this.syncTargetLinesField();
    if (this.hasSortFieldTarget) {
      this.sortFieldTarget.classList.toggle("hidden", !grouped);
      // Cleared, not just hidden: a sort with no dimension is refused server-side, so a stale value from
      // a previous breakdown would fail the preview and the save after a normal UI sequence.
      if (!grouped && this.hasSortTarget && this.sortTarget.value !== "") {
        this.resetSelect(this.sortFieldTarget, this.sortTarget, "");
      }
    }
    if (!this.hasChartTypeTarget) return;

    // Mirrors ValidatesRealtimeChartConfig: a ranked comparison emits wide columns only a table reads, an
    // unranked one long rows only a bar or line reads.
    const companion = this.selectedCompanion();
    const ranked = this.hasLimitTarget && this.limitTarget.value !== "";
    const allowed = companion ? (ranked ? ["table"] : ["bar", "line"]) : null;

    const field = this.hasChartTypeFieldTarget ? this.chartTypeFieldTarget : this.element;
    field.querySelectorAll("[data-grouped]").forEach((option) => {
      const value = option.dataset.selectValue;
      const fitsShape = (option.dataset.grouped === "true") === grouped;
      const fitsCompanion = !allowed || value === "" || allowed.includes(value);
      const hideable = option.closest("[data-ui--filter-target='item']") || option;
      hideable.classList.toggle("hidden", !fitsShape || !fitsCompanion);
    });
    // A pick that no longer fits the shape is refused on save, so it falls back to the concrete
    // default rather than a blank -- the shape is knowable from the controls.
    const current = this.chartTypeTarget.value;
    const stillValid = current === "" ||
      ((current === "scalar") !== grouped && (!allowed || allowed.includes(current)));
    if (!stillValid) this.resetSelect(field, this.chartTypeTarget, this.defaultCardType());
    // Last dependency wins: seriesChanged, companionChanged and limitChanged all reach here without
    // going through syncDependents, and each can leave the heading over an empty section.
    this.syncRefineHeading();
  }

  // The pane and its heading are always there; only the placeholder swaps with the fields. Reads the
  // fields rather than re-deriving the rules that hide them, so it cannot drift from them.
  //
  // NEITHER THE GRID NOR THE PANE MOVES. Both used to collapse when no field applied, so every change
  // of breakdown reflowed the dialog around the cursor.
  syncRefineHeading() {
    if (!this.hasRefineEmptyTarget) return;

    const fields = ["seriesField", "companionField", "limitField", "sortField", "targetLinesField"];
    const anyVisible = fields.some((name) => {
      const target = this[`${name}Target`];
      const has = this[`has${name[0].toUpperCase()}${name.slice(1)}Target`];
      return has && target && !target.classList.contains("hidden");
    });
    this.refineEmptyTarget.classList.toggle("hidden", anyVisible);
  }

  // A reference line needs an axis to sit against: a table has none, and a scalar draws one number.
  syncTargetLinesField() {
    if (!this.hasTargetLinesFieldTarget) return;

    const picked = this.hasChartTypeTarget ? this.chartTypeTarget.value : "";
    const effective = picked || this.defaultCardType();
    const drawable = effective === "bar" || effective === "line";
    this.targetLinesFieldTarget.classList.toggle("hidden", !drawable);
    // Cleared, not just hidden, and EVERY input in the section rather than the visible value alone: the
    // payload reads them directly, so a line left behind by a switch to Table would still be sent and
    // saved on a shape that cannot draw it -- including the preserved ones a multi-line chart carries,
    // which the author cannot see to remove.
    if (!drawable) {
      this.targetLinesFieldTarget
        .querySelectorAll("[name^='target_lines']")
        .forEach((input) => { input.value = ""; });
    }
  }

  // A rate has no parts that add up, so there is no remainder to fold -- and MetricQuery refuses the
  // pairing. Hidden here rather than omitted server-side, because the metric can change after render.
  syncRollupField() {
    if (!this.hasRollupFieldTarget) return;

    const metric = this.selectedMetric();
    const rollable = !!metric && metric.splittable;
    this.rollupFieldTarget.classList.toggle("hidden", !rollable);
    if (!rollable && this.hasRollupTarget) this.rollupTarget.checked = false;
  }

  syncGrainNote() {
    if (!this.hasGrainNoteTarget) return;

    const metric = this.selectedMetric();
    const lineGrain = !!metric && this.unboundedValue.some((d) => metric.dimensions.includes(d));
    this.grainNoteTarget.classList.toggle("hidden", lineGrain);
  }

  syncName() {
    if (!this.nameIsGenerated) return;

    this.nameTarget.value = this.generatedName();
  }

  // Keyed on the PAIR, matching how the server precomputed them: a split changes the name, so the
  // dimension alone is not enough to look one up.
  generatedName() {
    return this.selectedMetric().names[`${this.selectedDimension()}|${this.selectedSeries()}`] || "";
  }

  // Through the select's OWN setter, not by writing the hidden input and display text: the component
  // keeps its selection in a private field, and reaching past it leaves that field stale -- the next
  // click on the value it still believes is selected reads as a DESELECT and clears instead.
  // `value` defaults to "" -- the clear the series/companion callers want. "Show as" and the limit pass a
  // concrete value instead, because those controls have no blank option to fall back to.
  resetSelect(field, hiddenInput, value = "") {
    const root = field.querySelector('[data-controller~="select"]');
    const select = root &&
      this.application.getControllerForElementAndIdentifier(root, "select");
    if (!select) {
      if (hiddenInput) hiddenInput.value = value;
      return;
    }
    // GUARDED: setSelectedValue dispatches `change` on the hidden input, which is the same event our
    // own seriesChanged/companionChanged listen for -- and those clear each other, so an unguarded
    // reset ping-pongs between the two handlers.
    this.clearing = true;
    try {
      select.setSelectedValue(value);
    } finally {
      this.clearing = false;
    }
  }

  clearSeries() {
    this.resetSelect(this.seriesFieldTarget, this.hasSeriesTarget ? this.seriesTarget : null);
  }

  clearCompanion() {
    this.resetSelect(this.companionFieldTarget, this.hasCompanionTarget ? this.companionTarget : null);
  }

  selectedMetric() {
    return this.metricsValue.find((metric) => metric.key === this.metricTarget.value);
  }

  // Filtered on `disabled`: every metric's picker is rendered and all but one disabled, so an
  // unfiltered read finds a hidden metric's value.
  selectedDimension() {
    const active = this.dimensionTargets.find((input) => !input.disabled);
    return active ? active.value : "";
  }

  selectedSeries() {
    return this.hasSeriesTarget ? this.seriesTarget.value : "";
  }

  selectedCompanion() {
    return this.hasCompanionTarget ? this.companionTarget.value : "";
  }

  // "value:label", the string form normalize_target_lines parses. Both surfaces send it through here so
  // a label typed on one cannot be dropped by the other.
  // Every goal line the form holds, not just the editable pair: a chart carrying more lines than the
  // form renders keeps the rest in hidden inputs, and a preview that omitted them drew a different
  // chart from the one Save writes. Newline-separated "value:label", which normalize_target_lines parses.
  selectedTargetLines() {
    const values = [...this.element.querySelectorAll("[name='target_lines[][value]']")];
    const labels = [...this.element.querySelectorAll("[name='target_lines[][label]']")];

    return values
      .map((input, index) => {
        const value = input.value.trim();
        if (value === "") return null;

        const label = labels[index] ? labels[index].value.trim() : "";
        return label ? `${value}:${label}` : value;
      })
      .filter(Boolean)
      .join("\n");
  }

  selectedLimit() {
    if (!this.hasLimitTarget) return "";

    return this.unboundedValue.includes(this.selectedDimension()) ? this.limitTarget.value : "";
  }

  schedulePreview() {
    clearTimeout(this.previewTimeout);
    this.previewTimeout = setTimeout(() => this.loadPreview(), this.constructor.PREVIEW_DEBOUNCE_MS);
  }
}
